'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';

import type { CommunityMember } from '@zealy/queries';
import { Alert, Button, TaskCard, TaskCardHint } from '@zealy/design-system';
import { QuestionFilled, RefreshCwLine, TwitterFilled } from '@zealy/icons';
import { apiV2, useAuthenticatedUser, useCommunity } from '@zealy/queries';
import { roleIsAtLeast } from '@zealy/utils';

import { toast } from '#components/Toaster';
import { TASK_CONFIG } from '#constants/quests/Task.constants';
import { useOauth2 } from '#hooks/useOAuth2';

export const ConnectTwitter = () => {
  const twitterAuth = useOauth2('twitter');
  const { subdomain } = useParams<{ subdomain: string }>();
  const { data: community } = useCommunity(subdomain);
  const user = useAuthenticatedUser<CommunityMember>();
  const t = useTranslations('settings.linked-accounts');

  const [isRefreshing, setIsRefreshing] = useState(false);

  const requiresBlue = community?.requiredFields?.twitterBlue;

  // Get user's Twitter account info
  const accounts = user.data && 'accounts' in user.data ? user.data.accounts : [];
  const twitterAccount = accounts?.find(a => a.accountType === 'twitter');
  const hasTwitterConnected = !!twitterAccount && twitterAccount.tokenStatus === 'valid';
  const hasTwitterPremium = hasTwitterConnected && twitterAccount.premiumAccount;
  const premiumAccountNotSet = hasTwitterConnected && !('premiumAccount' in twitterAccount);

  // Determine what to show
  const shouldShowConnectButton = !requiresBlue || !hasTwitterConnected;
  const shouldShowWarningAlert =
    requiresBlue && hasTwitterConnected && !hasTwitterPremium && !premiumAccountNotSet;
  const shouldShowInfoAlert = requiresBlue && hasTwitterConnected && premiumAccountNotSet;

  const title = `${t('connect')} ${t('twitter.title')}`;

  const isAdmin = roleIsAtLeast(user?.data?.role, 'admin');
  const shouldShowHint = isAdmin && (community?.requiredFields?.linkTwitter || requiresBlue);

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      const response = await apiV2.auth.checkTwitterPremiumStatus();

      if (response.status === 200) {
        toast.success('Twitter status refreshed successfully');
        // Refetch user data to get updated premium status
        user.refetch();
      }
    } catch (error) {
      console.error('Error refreshing Twitter status:', error);
      toast.error('Failed to refresh Twitter status. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // If we don't require Twitter Blue, show original component
  if (!requiresBlue) {
    return (
      <TaskCard
        title={title}
        type="twitter"
        className="px-200 py-300"
        icon={TASK_CONFIG.twitterFollow.icon}
        hint={
          shouldShowHint && (
            <TaskCardHint
              icon={<QuestionFilled />}
              tooltip={t.rich('twitter.hint', {
                link: chunks => (
                  <Link href={`/cw/${subdomain}/settings/security#access`} className="underline">
                    {chunks}
                  </Link>
                ),
              })}
            >
              {t('twitter.hint-trigger')}
            </TaskCardHint>
          )
        }
      >
        <Button
          leftIcon={<TwitterFilled />}
          size="lg"
          className="w-full text-white bg-task-type-twitter hover:bg-task-type-twitter-hover active:bg-task-type-twitter-press"
          onClick={() => twitterAuth.open()}
        >
          {title}
        </Button>
      </TaskCard>
    );
  }

  // If we require Twitter Blue but user hasn't connected Twitter yet
  if (shouldShowConnectButton) {
    return (
      <TaskCard
        title={title}
        type="twitter"
        className="px-200 py-300"
        icon={TASK_CONFIG.twitterFollow.icon}
        hint={
          shouldShowHint && (
            <TaskCardHint
              icon={<QuestionFilled />}
              tooltip={t.rich('twitter.hint', {
                link: chunks => (
                  <Link href={`/cw/${subdomain}/settings/security#access`} className="underline">
                    {chunks}
                  </Link>
                ),
              })}
            >
              {t('twitter.hint-trigger')}
            </TaskCardHint>
          )
        }
      >
        <Button
          leftIcon={<TwitterFilled />}
          size="lg"
          className="w-full text-white bg-task-type-twitter hover:bg-task-type-twitter-hover active:bg-task-type-twitter-press"
          onClick={() => twitterAuth.open()}
        >
          {title}
        </Button>
      </TaskCard>
    );
  }

  // If we require Twitter Blue and user has Twitter connected but premium status is unknown
  if (shouldShowInfoAlert) {
    return (
      <Alert
        variant="info"
        title="Check Twitter Premium Status"
        description="We need to verify if your connected Twitter account has a premium subscription. Click the button below to check your Twitter Blue status."
        actions={
          <Button
            variant="muted"
            size="sm"
            leftIcon={<RefreshCwLine />}
            onClick={handleRefreshStatus}
            loading={isRefreshing}
          >
            Check Twitter Blue Status
          </Button>
        }
      />
    );
  }

  // If we require Twitter Blue and user has Twitter connected but it's not premium
  if (shouldShowWarningAlert) {
    return (
      <Alert
        variant="warning"
        title="Twitter Premium Required"
        description="Your connected Twitter account doesn't have a premium subscription. You need Twitter Premium (Twitter Blue) to access this quest."
        actions={
          <div className="flex gap-100 flex-wrap">
            <Button
              variant="muted"
              size="sm"
              leftIcon={<RefreshCwLine />}
              onClick={handleRefreshStatus}
              loading={isRefreshing}
            >
              Refresh Status
            </Button>
            <Button
              variant="muted"
              size="sm"
              leftIcon={<TwitterFilled />}
              onClick={() => twitterAuth.open()}
            >
              Connect New Account
            </Button>
          </div>
        }
      />
    );
  }

  // If user has premium Twitter, component won't be rendered (covered by quest access logic)
  return null;
};
